import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { message } from 'antd';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { authService } from '../services/authService';
import { UserInfo, ApiMenuItem } from '../types';

interface AuthContextType {
  user: UserInfo | null;
  token: string | null;
  isAuthenticated: boolean;
  menus: ApiMenuItem[];
  permissions: string[];
  login: (username: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useLocalStorage<UserInfo | null>('userInfo', null);
  const [token, setToken] = useLocalStorage<string | null>('accessToken', null);
  const [menus, setMenus] = useLocalStorage<ApiMenuItem[]>('userMenus', []);
  const [permissions, setPermissions] = useLocalStorage<string[]>('userPermissions', []);
  const [loading, setLoading] = useState(true);

  const isAuthenticated = !!token && !!user;

  const login = async (username: string, password: string, rememberMe: boolean = false) => {
    try {
      // 对密码进行 base64 编码
      const encodedPassword = btoa(password);
      
      const response = await authService.login({
        username,
        password: encodedPassword,
        rememberMe,
      });
      
      setToken(response.accessToken);
      setUser(response.userInfo);
      setMenus(response.menus);
      setPermissions(response.permissions);
      
      // message.success('登录成功');
    } catch (error) {
      message.error('登录失败');
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
      setToken(null);
      setUser(null);
      setMenus([]);
      setPermissions([]);
      message.success('已退出登录');
    } catch (error) {
      // 即使登出失败，也要清除本地数据
      setToken(null);
      setUser(null);
      setMenus([]);
      setPermissions([]);
      message.error('退出登录失败');
    }
  };

  const hasPermission = (permission: string): boolean => {
    return permissions.includes(permission);
  };

  const hasAnyPermission = (permissionList: string[]): boolean => {
    return permissionList.some(permission => permissions.includes(permission));
  };

  const hasAllPermissions = (permissionList: string[]): boolean => {
    return permissionList.every(permission => permissions.includes(permission));
  };

  // 验证token有效性
  const validateToken = async () => {
    
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const isValid = await authService.validateToken(token);
      
      if (!isValid) {
        setToken(null);
        setUser(null);
        setMenus([]);
        setPermissions([]);
        // 注意：这里不再显示错误消息，因为HTTP工具类已经处理了
      }
    } catch (error) {
      // 如果是网络错误或其他非认证错误，不要清除认证数据
      // 只有在明确是认证失败时才清除
      if (error instanceof Error && error.message.includes('401')) {
        setToken(null);
        setUser(null);
        setMenus([]);
        setPermissions([]);
        // 注意：这里不再显示错误消息，因为HTTP工具类已经处理了
      }
    } finally {
      setLoading(false);
    }
  };

  // 临时禁用token验证逻辑，避免刷新页面时验证失败
  // useEffect(() => {
  //   validateToken();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [token]);

  // 只在组件初始化时验证一次token
  useEffect(() => {
    
    if (token && loading) {
      validateToken();
    } else {
      setLoading(false);
    }
  }, []); // 只在组件挂载时执行一次

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated,
    menus,
    permissions,
    login,
    logout,
    loading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 